import { FontCategory, FontInfo } from "../../types";

// Only sans-serif and monospace are allowed in the font system
export const FONT_CATEGORIES = {
  "sans-serif": {
    label: "Sans Serif",
    fallback: "ui-sans-serif, system-ui, sans-serif",
  },
  monospace: {
    label: "Monospace",
    fallback:
      "ui-monospace, 'Cascadia Code', 'Source Code Pro', Menlo, Monaco, Consolas, 'Courier New', monospace",
  },
} as const;

// Allowed font categories for the entire app - only sans-serif and monospace
export const ALLOWED_FONT_CATEGORIES: FontCategory[] = ["sans-serif", "monospace"];

// Local fonts bundled in public/fonts/ — these don't need Google Fonts loading
export const LOCAL_FONT_NAMES = [
  "Circular",
  "PPMori",
  "Open Runde",
  "Questrial",
  "Zalando Sans",
] as const;

// Local fonts that are available without Google Fonts (bundled in public/fonts/)
export const LOCAL_FONTS: FontInfo[] = [
  {
    family: "Circular",
    category: "sans-serif",
    variants: ["400", "500", "700", "900"],
    variable: false,
  },
  {
    family: "PPMori",
    category: "sans-serif",
    variants: ["200", "400", "600"],
    variable: false,
  },
  {
    family: "Open Runde",
    category: "sans-serif",
    variants: ["400", "500", "600", "700"],
    variable: false,
  },
  {
    family: "Questrial",
    category: "sans-serif",
    variants: ["400"],
    variable: false,
  },
  {
    family: "Zalando Sans",
    category: "sans-serif",
    variants: ["300"],
    variable: false,
  },
];

// Check if a font is a local font (bundled in public/fonts/)
export function isLocalFont(fontFamily: string): boolean {
  return LOCAL_FONT_NAMES.some(
    (name) => name.toLowerCase() === fontFamily.toLowerCase()
  );
}

// Fallback fonts if the JSON file doesn't exist
// Only sans-serif and monospace fonts are included
export const FALLBACK_FONTS: FontInfo[] = [
  // Local fonts first (always available, no network needed)
  ...LOCAL_FONTS,
  // Sans Serif fonts (from Google Fonts)
  {
    family: "Inter",
    category: "sans-serif",
    variants: ["400", "600", "700"],
    variable: true,
  },
  {
    family: "Roboto",
    category: "sans-serif",
    variants: ["400", "600", "700"],
    variable: false,
  },
  {
    family: "Open Sans",
    category: "sans-serif",
    variants: ["400", "600", "700"],
    variable: true,
  },
  {
    family: "Poppins",
    category: "sans-serif",
    variants: ["400", "600", "700"],
    variable: false,
  },
  {
    family: "Montserrat",
    category: "sans-serif",
    variants: ["400", "600", "700"],
    variable: false,
  },
  {
    family: "Outfit",
    category: "sans-serif",
    variants: ["400", "600", "700"],
    variable: false,
  },
  {
    family: "Plus Jakarta Sans",
    category: "sans-serif",
    variants: ["400", "600", "700"],
    variable: false,
  },
  {
    family: "DM Sans",
    category: "sans-serif",
    variants: ["400", "600", "700"],
    variable: true,
  },
  {
    family: "Geist",
    category: "sans-serif",
    variants: ["400", "600", "700"],
    variable: true,
  },
  {
    family: "Oxanium",
    category: "sans-serif",
    variants: ["400", "600", "700"],
    variable: false,
  },
  // Monospace fonts
  {
    family: "JetBrains Mono",
    category: "monospace",
    variants: ["400", "600", "700"],
    variable: true,
  },
  {
    family: "Fira Code",
    category: "monospace",
    variants: ["400", "600", "700"],
    variable: true,
  },
  {
    family: "Source Code Pro",
    category: "monospace",
    variants: ["400", "600", "700"],
    variable: false,
  },
  {
    family: "IBM Plex Mono",
    category: "monospace",
    variants: ["400", "600", "700"],
    variable: false,
  },
  {
    family: "Roboto Mono",
    category: "monospace",
    variants: ["400", "600", "700"],
    variable: false,
  },
  {
    family: "Space Mono",
    category: "monospace",
    variants: ["400", "600", "700"],
    variable: false,
  },
  {
    family: "Geist Mono",
    category: "monospace",
    variants: ["400", "600", "700"],
    variable: true,
  },
];

// Build font-family value for CSS
// e.g., font: "Inter", category: "sans-serif" -> "Inter, ui-sans-serif, system-ui, sans-serif"
export function buildFontFamily(fontFamily: string, category: FontCategory): string {
  return `${fontFamily}, ${SYSTEM_FONTS_FALLBACKS[category]}`;
}

// Extract font family name from CSS font-family value
// e.g., "Inter, ui-sans-serif, system-ui, sans-serif" -> "Inter"
export function extractFontFamily(fontFamilyValue: string): string | null {
  if (!fontFamilyValue) return null;

  // Split by comma and get the first font
  const firstFont = fontFamilyValue.split(",")[0].trim();

  // Remove quotes if present
  const cleanFont = firstFont.replace(/['"]/g, "");

  // Skip system fonts
  if (SYSTEM_FONTS.includes(cleanFont.toLowerCase())) return null;
  return cleanFont;
}

// Get default weights for a font based on available variants
export function getDefaultWeights(variants: string[]): string[] {
  const weightMap = ["100", "200", "300", "400", "500", "600", "700", "800", "900"];
  const availableWeights = variants.filter((variant) => weightMap.includes(variant));

  if (availableWeights.length === 0) return ["400"]; // Fallback to normal weight

  const preferredWeights = ["400", "500", "600", "700"];
  const selectedWeights = preferredWeights.filter((weight) => availableWeights.includes(weight));

  // If none of the preferred weights are available, use the first two available
  if (selectedWeights.length === 0) {
    const fallbackWeights = availableWeights.slice(0, 2);
    return fallbackWeights.sort((a, b) => parseInt(a) - parseInt(b));
  }

  // Return up to 4 weights, starting with preferred ones
  const finalWeights = [
    ...selectedWeights,
    ...availableWeights.filter((w) => !selectedWeights.includes(w)),
  ].slice(0, 4);

  // Sort weights numerically for Google Fonts API requirement
  return finalWeights.sort((a, b) => parseInt(a) - parseInt(b));
}

// Check if a font is available using the native document.fonts API
export function isFontLoaded(family: string, weight = "400"): boolean {
  if (typeof document === "undefined" || !document.fonts) return false;

  // Use the native FontFaceSet.check() method
  return document.fonts.check(`${weight} 16px "${family}"`);
}

// Wait for a font to load using the native document.fonts API
export async function waitForFont(
  family: string,
  weight = "400",
  timeout = 3000
): Promise<boolean> {
  if (typeof document === "undefined" || !document.fonts) return false;

  const font = `${weight} 16px "${family}"`;

  try {
    // Use the native document.fonts.load() method
    await Promise.race([
      document.fonts.load(font),
      new Promise((_, reject) => setTimeout(() => reject(new Error("Timeout")), timeout)),
    ]);

    return document.fonts.check(font);
  } catch {
    return false;
  }
}

export const SYSTEM_FONTS = [
  "ui-sans-serif",
  "ui-serif",
  "ui-monospace",
  "system-ui",
  "sans-serif",
  "serif",
  "monospace",
  "cursive",
  "fantasy",
];

export const SYSTEM_FONTS_FALLBACKS = {
  "sans-serif": "ui-sans-serif, sans-serif, system-ui",
  serif: "ui-serif, serif",
  monospace: "ui-monospace, monospace",
  display: "ui-serif, serif",
  handwriting: "cursive",
};
